/**
 * 认证相关的工具函数
 */

const TOKEN_KEY = 'token';
const USERNAME_KEY = 'username';

/**
 * 获取保存的 token
 */
export const getToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
};

/**
 * 保存 token
 */
export const setToken = (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
};

/**
 * 删除 token（登出时使用）
 */
export const removeToken = (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USERNAME_KEY);
};

/**
 * 获取保存的用户名
 */
export const getUsername = (): string | null => {
    return localStorage.getItem(USERNAME_KEY);
};

/**
 * 保存用户名
 */
export const setUsername = (username: string): void => {
    localStorage.setItem(USERNAME_KEY, username);
};

/**
 * 检查用户是否已登录
 */
export const isAuthenticated = (): boolean => {
    return !!getToken();
};

/**
 * 检查 token 是否过期
 * @param {number} exp token 的过期时间（Unix 时间戳，秒）
 * @returns {boolean} 如果 token 已过期返回 true，否则返回 false
 */
export const isTokenExpired = (exp: number): boolean => {
    if (!exp) return true; // 如果没有过期时间，认为已过期
    const currentTime = Math.floor(Date.now() / 1000); // 当前时间（秒）
    return currentTime >= exp; // 如果当前时间 >= 过期时间，则已过期
};

/**
 * 检查 token 是否即将过期（在指定秒数内过期）
 * @param {number} exp token 的过期时间（Unix 时间戳，秒）
 * @param {number} bufferSeconds 提前多少秒认为即将过期（默认 300 秒，即 5 分钟）
 * @returns {boolean} 如果 token 即将过期返回 true，否则返回 false
 */
export const isTokenExpiringSoon = (exp: number, bufferSeconds: number = 300): boolean => {
    if (!exp) return true;
    const currentTime = Math.floor(Date.now() / 1000);
    return (exp - currentTime) <= bufferSeconds; // 如果剩余时间 <= bufferSeconds，则认为即将过期
};

/**
 * 从 token 中解析 payload（不验证签名，仅用于获取信息）
 * @param {string} token JWT token
 * @returns {Object | null} 解析出的 payload，如果解析失败返回 null
 */
export const decodeToken = (token: string): any | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;

        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch (error) {
        console.error('解析 token 失败:', error);
        return null;
    }
};

/**
 * 检查当前保存的 token 是否过期
 * @returns {boolean} 如果 token 已过期或不存在返回 true，否则返回 false
 */
export const isCurrentTokenExpired = (): boolean => {
    const token = getToken();
    if (!token) return true;

    const payload = decodeToken(token);
    if (!payload || !payload.exp) return true;

    return isTokenExpired(payload.exp);
};

